import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getJobById, updateJob, retryJob, deleteJob } from '../Config/jobsService';
import {
  formatEasternDateTime,
  fromDateTimeLocalInEastern,
  toDateTimeLocalInEastern,
} from '../Utils/dateTime';

function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    nextRunAt: '',
    disabled: false,
    dataStr: '',
  });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await getJobById(id);
        if (res.success) {
          setJob(res.data);
          setEditForm({
            nextRunAt: toDateTimeLocalInEastern(res.data.nextRunAt),
            disabled: !!res.data.disabled,
            dataStr: JSON.stringify(res.data.data || {}, null, 2),
          });
        } else {
          toast.error(res.message || 'Job not found');
          navigate('/jobs');
        }
      } catch (err) {
        toast.error('Failed to load job');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    const body = {};
    if (editForm.nextRunAt) body.nextRunAt = fromDateTimeLocalInEastern(editForm.nextRunAt);
    body.disabled = editForm.disabled;
    try {
      const data = JSON.parse(editForm.dataStr || '{}');
      body.data = data;
    } catch {
      toast.error('Invalid JSON in data');
      return;
    }
    setUpdating(true);
    try {
      const res = await updateJob(id, body);
      if (res.success) {
        toast.success('Job updated');
        setJob(res.data);
        setEditMode(false);
      } else {
        toast.error(res.message || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update job');
    } finally {
      setUpdating(false);
    }
  };

  const handleRetry = async () => {
    try {
      const res = await retryJob(id);
      if (res.success) {
        toast.success('Job retried');
        setJob(res.data);
      } else {
        toast.error(res.message || 'Failed to retry');
      }
    } catch (err) {
      toast.error('Failed to retry job');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Cancel and remove this job? This cannot be undone.')) return;
    try {
      const res = await deleteJob(id);
      if (res.success) {
        toast.success('Job canceled');
        navigate('/jobs');
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete job');
    }
  };

  const getStatusColor = (status) => {
    const map = {
      scheduled: 'bg-blue-100 text-blue-800',
      running: 'bg-amber-100 text-amber-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      disabled: 'bg-gray-100 text-gray-700',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (d) => formatEasternDateTime(d);

  if (loading || !job) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <main className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />

      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/jobs')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold">{job.name}</h1>
              {job.eventType && (
                <span className="px-2 py-1 text-sm bg-slate-100 text-slate-700 rounded">
                  {job.eventType}
                </span>
              )}
              <span className={`px-2 py-1 text-sm font-semibold rounded-full ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
              {job.disabled && <span className="text-amber-600 text-sm">(disabled)</span>}
            </div>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {job.status === 'failed' && (
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 font-medium"
                >
                  Retry
                </button>
              )}
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                >
                  Edit
                </button>
              ) : null}
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium"
              >
                Delete
              </button>
            </div>
          </div>

          {editMode ? (
            <form onSubmit={handleUpdate} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Next run at (Eastern time)</label>
                <input
                  type="datetime-local"
                  value={editForm.nextRunAt}
                  onChange={(e) => setEditForm((f) => ({ ...f, nextRunAt: e.target.value }))}
                  className="w-full max-w-xs px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.disabled}
                    onChange={(e) => setEditForm((f) => ({ ...f, disabled: e.target.checked }))}
                  />
                  Disabled
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job data (JSON)</label>
                <textarea
                  value={editForm.dataStr}
                  onChange={(e) => setEditForm((f) => ({ ...f, dataStr: e.target.value }))}
                  rows={12}
                  className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {updating ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Job ID</div>
                  <div className="text-sm font-mono">{job.id}</div>
                </div>
                {job.eventType && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase">Event type</div>
                    <div className="text-sm">{job.eventType}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Created</div>
                  <div className="text-sm">{formatDate(job.createdAt)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Next run</div>
                  <div className="text-sm">{formatDate(job.nextRunAt)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Last run</div>
                  <div className="text-sm">{formatDate(job.lastRunAt)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Last finished</div>
                  <div className="text-sm">{formatDate(job.lastFinishedAt)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase">Fail count</div>
                  <div className="text-sm">{job.failCount ?? 0}</div>
                </div>
                {job.failReason && (
                  <div className="md:col-span-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase">Fail reason</div>
                    <div className="text-sm text-red-600 mt-1 p-2 bg-red-50 rounded">{job.failReason}</div>
                  </div>
                )}
                {job.failedAt && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase">Failed at</div>
                    <div className="text-sm">{formatDate(job.failedAt)}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Job data</div>
                <pre className="p-4 bg-gray-50 rounded-lg overflow-auto text-sm font-mono">
                  {JSON.stringify(job.data || {}, null, 2)}
                </pre>
              </div>

              {job.result != null && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Result</div>
                  <pre className="p-4 bg-gray-50 rounded-lg overflow-auto text-sm font-mono">
                    {typeof job.result === 'object' ? JSON.stringify(job.result, null, 2) : String(job.result)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default JobDetails;
